// src/hooks/useJustifiedRows.js
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import justifiedLayoutImport from 'justified-layout';

/**
 * useJustifiedRows (v4 – enterprise-optimized + robust fallback)
 *
 * - Stable first measure (useLayoutEffect), RO+rAF throttled resizes
 * - Ignores tiny width jitters (<2px)
 * - ESM/CJS interop guard for justified-layout
 * - Try/catch + explicit diagnostics
 * - Guaranteed fallback row-packer so UI never deadlocks
 * - `ready` flag flipped by both success path and a safety useEffect
 */
export default function useJustifiedRows(
  items,
  {
    targetRowHeight = 200,
    containerPadding = 0,
    boxSpacing = 24,
    showWidows = false,
    maxNumRows,
  } = {}
) {
  // Version banner — confirm this actually runs
  // (log only once per mount to avoid console storms)
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[useJustifiedRows v4] init', { items: items?.length ?? 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [ready, setReady] = useState(false);
  const rafRef = useRef(0);

  // Normalize spacing to { horizontal, vertical }
  const spacing = useMemo(() => {
    if (typeof boxSpacing === 'number') {
      return { horizontal: boxSpacing, vertical: boxSpacing };
    }
    const h = Math.max(0, Number(boxSpacing?.horizontal ?? 0));
    const v = Math.max(0, Number(boxSpacing?.vertical ?? 0));
    return { horizontal: h, vertical: v };
  }, [boxSpacing]);

  // First width read (sync)
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let w = Math.max(0, Math.floor(el.getBoundingClientRect().width));
    if (w === 0 && el.parentElement) {
      w = Math.max(0, Math.floor(el.parentElement.getBoundingClientRect().width));
    }
    if (w === 0) {
      requestAnimationFrame(() => {
        const w2 = Math.max(0, Math.floor(el.getBoundingClientRect().width));
        if (w2 > 0) setContainerWidth(w2);
      });
    } else {
      setContainerWidth(w);
    }
    setReady(false);
  }, []);

  // rAF-throttled ResizeObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onResize = (width) => {
      let widthChanged = false;
      setContainerWidth((prev) => {
        if (Math.abs(prev - width) < 2) return prev;
        widthChanged = true;
        return width;
      });
      if (widthChanged) setReady(false);
    };

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const w = Math.max(0, Math.floor(entry.contentRect.width));
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => onResize(w));
    });

    ro.observe(el);
    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, []);

  // Aspect ratios array
  const aspects = useMemo(
    () => items.map((it) => Math.max(0.01, Number(it.aspect || 1))),
    [items]
  );

  // Resolve justified-layout function (interop)
  const jl = useMemo(() => {
    const fn =
      typeof justifiedLayoutImport === 'function'
        ? justifiedLayoutImport
        : typeof justifiedLayoutImport?.default === 'function'
        ? justifiedLayoutImport.default
        : null;
    if (!fn) {
      // eslint-disable-next-line no-console
      console.warn('[useJustifiedRows v4] could not resolve justified-layout; using fallback packer.');
    }
    return fn;
  }, []);

  // Fallback row packer (greedy rows)
  const fallbackPack = useMemo(() => {
    return (cw, H, gapX) => {
      const boxes = [];
      let left = 0;
      let top = 0;
      let rowNatW = 0;
      let row = [];

      const flush = (justify) => {
        if (!row.length) return;
        const gaps = gapX * (row.length - 1);
        const natW = rowNatW;
        const scale = justify && natW > 0 ? (cw - gaps) / natW : 1;
        const h = Math.max(1, Math.round(H * scale));

        left = 0;
        for (const cell of row) {
          const w = Math.max(1, Math.round(cell.a * h));
          boxes.push({ left, top, width: w, height: h, idx: cell.idx });
          left += w + gapX;
        }

        row = [];
        rowNatW = 0;
        top += h + spacing.vertical;
      };

      aspects.forEach((a, idx) => {
        const natW = a * H;
        if (row.length && (rowNatW + natW + gapX * row.length) > cw) {
          flush(true);
        }
        row.push({ a, idx });
        rowNatW += natW;
      });
      flush(false); // last row ragged

      const containerHeight = boxes.length > 0 ? top - spacing.vertical : 0;
      return { boxes, containerHeight: Math.max(0, containerHeight) };
    };
  }, [aspects, spacing.vertical]);

  // Compute layout geometry
  const layout = useMemo(() => {
    if (!containerWidth || aspects.length === 0) {
      return { boxes: [], containerHeight: 0 };
    }

    // 1) Try library first
    if (jl) {
      try {
        const result = jl(aspects, {
          containerWidth,
          containerPadding,
          boxSpacing: spacing.horizontal, // vertical spacing added by us
          targetRowHeight,
          targetRowHeightTolerance: 0.15,
          showWidows,
          maxNumRows,
        });

        const boxesRaw = Array.isArray(result?.boxes) ? result.boxes : [];
        if (boxesRaw.length > 0) {
          const boxes = [];
          let lastRowTop = -1;
          let rowIndex = -1;

          for (let i = 0; i < boxesRaw.length; i++) {
            const b = boxesRaw[i];
            if (b.top !== lastRowTop) {
              rowIndex += 1;
              lastRowTop = b.top;
            }
            const adjustedTop = b.top + rowIndex * spacing.vertical;

            boxes.push({
              ...b,
              top: adjustedTop,
              id: items[i]?.id ?? String(i),
              data: items[i]?.data ?? null,
              aspect: aspects[i],
            });
          }

          const totalRows = rowIndex + 1;
          const containerHeight =
            (result.containerHeight || 0) + Math.max(0, (totalRows - 1) * spacing.vertical);

          // NOTE: do NOT set state from inside useMemo/render.
          // ready is flipped in a dedicated effect below.
          return { boxes, containerHeight };
        }

        // eslint-disable-next-line no-console
        console.warn('[useJustifiedRows v4] justified-layout returned 0 boxes; falling back.', {
          containerWidth,
          targetRowHeight,
          aspectsLen: aspects.length,
          firstAspects: aspects.slice(0, 8),
          resultShape: typeof result,
          result,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[useJustifiedRows v4] justified-layout threw; falling back.', e);
      }
    }

    // 2) Fallback packer
    const fb = fallbackPack(containerWidth, targetRowHeight, spacing.horizontal);
    const stitched = {
      boxes: fb.boxes.map((b) => ({
        ...b,
        id: items[b.idx]?.id ?? String(b.idx),
        data: items[b.idx]?.data ?? null,
        aspect: aspects[b.idx],
      })),
      containerHeight: fb.containerHeight,
    };

    // NOTE: do NOT set state from inside useMemo/render.
    // ready is flipped in a dedicated effect below.
    return stitched;

  }, [
    jl,
    aspects,
    items,
    containerWidth,
    containerPadding,
    spacing.horizontal,
    spacing.vertical,
    targetRowHeight,
    showWidows,
    maxNumRows,
    fallbackPack,
  ]);

  // Flip ready=true when a new layout has been computed for the current inputs.
  // This avoids setState-in-useMemo loops and makes "ready" deterministic.
  const layoutKey = useMemo(() => {
    const boxesLen = layout?.boxes?.length ?? 0;
    const h = Math.round(layout?.containerHeight ?? 0);
    return `${containerWidth}|${items.length}|${boxesLen}|${h}|${targetRowHeight}|${spacing.horizontal}|${spacing.vertical}|${showWidows ? 1 : 0}|${maxNumRows ?? ''}`;
  }, [
    containerWidth,
    items.length,
    layout?.boxes?.length,
    layout?.containerHeight,
    targetRowHeight,
    spacing.horizontal,
    spacing.vertical,
    showWidows,
    maxNumRows,
  ]);

  useEffect(() => {
    if (containerWidth > 0 && items.length > 0) {
      // Only flip to true if we were "not ready" for this compute cycle
      setReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutKey]);


  return { containerRef, containerWidth, layout, ready };
}
